import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CompanyGigShift, GigType } from "model/Gig";
import { Api } from "services/api/api.service";
import { useAlertStore } from "stores/alertStore/alertStore";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { getShiftHours } from "utils/utils";
import { toOffsetDateTimeSafe } from "utils/date-time.logic";

import {
  resolveOverlappingShifts,
  OverlappingItem,
  getNewShifts,
} from "../logic/gigForm.overlap.logic";
import {
  submitCreateGig,
  submitCreateParentGig,
} from "./gigFormModal.submit.logic";
import {
  buildInitialFormData,
  buildInitialRecruitFormData,
  buildInitialScheduleSnapshot,
  buildScheduleSnapshot,
  hasEnabledModule,
  isPublicOrTeamType,
} from "./gigFormModal.state.logic";
import {
  getCreateDisabledReasonKeys,
  validateGigForm,
} from "../logic/gigForm.validation.logic";
import type {
  CreateGigType,
  FormData,
  GigFormModalProps,
  RecruitFormData,
  SelectedWorker,
  Tab,
} from "./gigFormModal.types";

const RECRUIT_TARGET_TYPE: CreateGigType = GigType.WORKPOOL;

type ShiftSummary = {
  totalMinutes: number;
  hasInvalidShiftTimes: boolean;
};

const getShiftSummary = (shifts: CompanyGigShift[]): ShiftSummary => {
  const validShifts: CompanyGigShift[] = shifts.filter((shift) => {
    const hasValidStartTime = toOffsetDateTimeSafe(shift.startTime) != null;
    const hasValidEndTime = toOffsetDateTimeSafe(shift.endTime) != null;
    return hasValidStartTime && hasValidEndTime;
  });

  const hasInvalidShiftTimes = validShifts.length !== shifts.length;
  const totalMinutes = Math.max(
    0,
    Math.floor(getShiftHours(...validShifts) * 60),
  );

  return { totalMinutes, hasInvalidShiftTimes };
};

const useShiftSummary = (
  shifts: CompanyGigShift[],
): {
  totalHours: number;
  totalMinutes: number;
  hasInvalidShiftTimes: boolean;
} => {
  const shiftSummary = useMemo(() => getShiftSummary(shifts), [shifts]);
  const totalHours = useMemo(
    () => Math.floor(shiftSummary.totalMinutes / 60),
    [shiftSummary.totalMinutes],
  );
  const totalMinutes = useMemo(
    () => shiftSummary.totalMinutes % 60,
    [shiftSummary.totalMinutes],
  );

  return {
    totalHours,
    totalMinutes,
    hasInvalidShiftTimes: shiftSummary.hasInvalidShiftTimes,
  };
};

const useDirtyState = (
  formData: FormData,
  selectedWorkers: SelectedWorker[],
  recruitFormData: RecruitFormData,
  initialFormData: FormData,
  initialRecruitFormData: RecruitFormData,
) => {
  const scheduleSnapshot = useMemo(() => {
    return buildScheduleSnapshot(formData, selectedWorkers);
  }, [formData, selectedWorkers]);

  const initialScheduleSnapshot = useMemo(() => {
    return buildInitialScheduleSnapshot(initialFormData);
  }, [initialFormData]);

  const recruitSnapshot = useMemo(
    () => JSON.stringify(recruitFormData),
    [recruitFormData],
  );

  const initialRecruitSnapshot = useMemo(
    () => JSON.stringify(initialRecruitFormData),
    [initialRecruitFormData],
  );

  const isScheduleDirty = useMemo(
    () => scheduleSnapshot !== initialScheduleSnapshot,
    [scheduleSnapshot, initialScheduleSnapshot],
  );

  const isRecruitDirty = useMemo(
    () => recruitSnapshot !== initialRecruitSnapshot,
    [recruitSnapshot, initialRecruitSnapshot],
  );

  return { isScheduleDirty, isRecruitDirty };
};

export type UseGigFormModalLogic = {
  canRecruit: boolean;
  changeTo: CreateGigType;
  formData: FormData;
  havePublicModule: boolean;
  haveTeamModule: boolean;
  isLoading: boolean;
  recruitFormData: RecruitFormData;
  selectedWorkers: SelectedWorker[];
  shiftListOpen: boolean;
  showAlert: boolean;
  showChangeTabAlert: boolean;
  showOverlappingError: boolean;
  overlapping: OverlappingItem[];
  isAddingMoreShifts: boolean;
  totalHours: number;
  totalMinutes: number;
  hasInvalidShiftTimes: boolean;
  isPublicRecruit: boolean;
  visibleForValue: GigType.WORKPOOL | GigType.PUBLIC;
  tab: Tab;
  isFormValid: boolean;
  createDisabledReasonKeys: string[];
  closeModal: () => void;
  handleChangeTab: (type: CreateGigType) => void;
  handleRequestClose: (
    _e?: unknown,
    reason?: "backdropClick" | "escapeKeyDown",
  ) => void;
  handleSubmitGigForm: () => void;
  requestTabChange: (to: Tab) => void;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  setRecruitFormData: React.Dispatch<React.SetStateAction<RecruitFormData>>;
  setSelectedWorkers: React.Dispatch<React.SetStateAction<SelectedWorker[]>>;
  setShiftListOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setShowAlert: React.Dispatch<React.SetStateAction<boolean>>;
  setShowChangeTabAlert: React.Dispatch<React.SetStateAction<boolean>>;
  setIsAddingMoreShifts: React.Dispatch<React.SetStateAction<boolean>>;
};

export const useGigFormModalLogic = (
  props: GigFormModalProps,
): UseGigFormModalLogic => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [, , alertDispatch] = useAlertStore();
  const { onCreated, setShowGigFormModal, showGigFormModal } = props;
  const [selectedWorkers, setSelectedWorkers] = useState<SelectedWorker[]>([]);
  const [shiftListOpen, setShiftListOpen] = useState(true);
  const [overlapping, setOverlapping] = useState<OverlappingItem[]>([]);
  const [showOverlappingError, setShowOverlappingError] = useState(false);
  const [showChangeTabAlert, setShowChangeTabAlert] = useState(false);
  const [isAddingMoreShifts, setIsAddingMoreShifts] = useState(false);
  const [changeTo, setChangeTo] = useState<CreateGigType>(GigType.SOLO);
  const [companyState, ,] = useCompanyStore();
  const [showAlert, setShowAlert] = useState(false);
  const overlapRequestIdRef = useRef<number>(0);
  const wasModalOpenRef = useRef<boolean>(false);
  const submitInFlightRef = useRef<boolean>(false);
  const resetOverlapState = useCallback((): void => {
    setOverlapping([]);
    setShowOverlappingError(false);
  }, []);

  const isPublicOrTeamGig = useMemo(
    () => isPublicOrTeamType(props.type),
    [props.type],
  );

  const havePublicModule = useMemo(() => {
    return hasEnabledModule(
      companyState.companySettings,
      "CompanyShowPublicGigModule",
    );
  }, [companyState.companySettings]);

  const haveTeamModule = useMemo(() => {
    return hasEnabledModule(
      companyState.companySettings,
      "CompanyShowTeamGigModule",
    );
  }, [companyState.companySettings]);

  const canRecruit = havePublicModule || haveTeamModule;

  const defaultTab: Tab =
    canRecruit && isPublicOrTeamGig ? "Recruit" : "Schedule";
  const initialTab: Tab =
    props.initialTab === "Recruit" && !canRecruit
      ? "Schedule"
      : (props.initialTab ?? defaultTab);
  const [tab, setTab] = useState<Tab>(initialTab);

  const initialFormData = useMemo(
    () => buildInitialFormData(props.shifts, props.type),
    [props.shifts, props.type],
  );

  const initialRecruitFormData = useMemo(
    () => buildInitialRecruitFormData(),
    [],
  );

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [recruitFormData, setRecruitFormData] = useState<RecruitFormData>(
    initialRecruitFormData,
  );

  useEffect(() => {
    if (!showGigFormModal) {
      wasModalOpenRef.current = false;
      return;
    }
    if (wasModalOpenRef.current) return;
    wasModalOpenRef.current = true;

    setFormData(initialFormData);
    setRecruitFormData(initialRecruitFormData);
    setSelectedWorkers([]);
    setTab(initialTab);
    resetOverlapState();
    setIsAddingMoreShifts(initialFormData.shifts.length === 0);
  }, [
    showGigFormModal,
    initialFormData,
    initialRecruitFormData,
    initialTab,
    resetOverlapState,
  ]);

  useEffect(() => {
    if (formData.shifts.length === 0) {
      setIsAddingMoreShifts(true);
    }
  }, [formData.shifts.length]);

  useEffect(() => {
    if (!canRecruit && tab === "Recruit") setTab("Schedule");
    if (!canRecruit && formData.type !== GigType.SOLO) {
      setFormData((curr) => ({ ...curr, type: GigType.SOLO }));
    }
  }, [canRecruit, tab, formData.type, setFormData]);

  useEffect(() => {
    if (!canRecruit || tab !== "Recruit") {
      return;
    }

    if (formData.type === GigType.SOLO) {
      setFormData((currentFormData) => ({
        ...currentFormData,
        type: RECRUIT_TARGET_TYPE,
      }));
    }
  }, [canRecruit, tab, formData.type, setFormData]);

  const { isScheduleDirty, isRecruitDirty } = useDirtyState(
    formData,
    selectedWorkers,
    recruitFormData,
    initialFormData,
    initialRecruitFormData,
  );

  const closeModal = useCallback((): void => {
    if (isLoading) return;
    setShowGigFormModal(false);
    setFormData(initialFormData);
    setRecruitFormData(initialRecruitFormData);
    setSelectedWorkers([]);
    resetOverlapState();
    setShowAlert(false);
    setShowChangeTabAlert(false);
    setTab(initialTab);
    setShiftListOpen(true);
    setIsAddingMoreShifts(initialFormData.shifts.length === 0);
  }, [
    isLoading,
    setShowGigFormModal,
    initialFormData,
    initialRecruitFormData,
    initialTab,
    resetOverlapState,
  ]);

  const handleCreateGig = useCallback(
    async (parentGigId?: number): Promise<void> => {
      const companyId = companyState.company?.id;
      if (!companyId) return;

      await submitCreateGig({
        alertDispatch,
        companyId,
        formData,
        onCreated,
        parentGigId,
        recruitFormData,
        selectedWorkers,
        setIsLoading,
        setShowGigFormModal,
        t,
      });
    },
    [
      alertDispatch,
      companyState.company?.id,
      formData,
      onCreated,
      recruitFormData,
      selectedWorkers,
      setShowGigFormModal,
      t,
    ],
  );

  const applyOverlapState = useCallback(
    (overlappingResults: OverlappingItem[]): void => {
      if (overlappingResults.length > 0) {
        setOverlapping(overlappingResults);
        setShowOverlappingError(true);
        setShiftListOpen(true);
        return;
      }

      setOverlapping([]);
      setShowOverlappingError(false);
    },
    [],
  );

  const resolveOverlapsForShifts = useCallback(
    async (shifts: CompanyGigShift[]): Promise<OverlappingItem[]> => {
      const companyId = companyState.company?.id;
      if (!companyId || selectedWorkers.length === 0 || shifts.length === 0) {
        return [];
      }

      return resolveOverlappingShifts({
        companyId,
        description: formData.description,
        selectedWorkers,
        shifts,
        getOverlappingGig: (id, data) =>
          Api().company.gig.getOverlappingGig(id, data),
      });
    },
    [companyState.company?.id, formData.description, selectedWorkers],
  );

  useEffect(() => {
    const requestId = overlapRequestIdRef.current + 1;
    overlapRequestIdRef.current = requestId;

    const runOverlapCheck = async (): Promise<void> => {
      const companyId = companyState.company?.id;
      if (!companyId) return;

      const newShifts = getNewShifts(formData.shifts ?? []);
      if (!newShifts.length || selectedWorkers.length === 0) {
        if (requestId !== overlapRequestIdRef.current) return;
        resetOverlapState();
        return;
      }

      const overlappingResults = await resolveOverlapsForShifts(newShifts);
      if (requestId !== overlapRequestIdRef.current) return;
      applyOverlapState(overlappingResults);
    };

    void runOverlapCheck();
  }, [
    companyState.company?.id,
    selectedWorkers,
    formData.shifts,
    resolveOverlapsForShifts,
    applyOverlapState,
    resetOverlapState,
  ]);

  const isFormValid = useMemo(
    () =>
      validateGigForm({
        formData,
        recruitFormData,
        selectedWorkers,
        showOverlappingError,
      }),
    [formData, recruitFormData, selectedWorkers, showOverlappingError],
  );
  const createDisabledReasonKeys = useMemo(() => {
    if (isLoading || isFormValid) return [] as string[];

    return getCreateDisabledReasonKeys({
      formData,
      recruitFormData,
      selectedWorkers,
      showOverlappingError,
    });
  }, [
    formData,
    isLoading,
    isFormValid,
    recruitFormData,
    selectedWorkers,
    showOverlappingError,
  ]);

  const { totalHours, totalMinutes, hasInvalidShiftTimes } = useShiftSummary(
    formData.shifts,
  );
  const isPublicRecruit = tab === "Recruit" && formData.type === GigType.PUBLIC;
  const visibleForValue =
    formData.type === GigType.PUBLIC ? GigType.PUBLIC : GigType.WORKPOOL;

  const handleCreateParentGig = async (): Promise<void> => {
    const companyId = companyState.company?.id;
    if (!companyId) return;
    await submitCreateParentGig({
      alertDispatch,
      companyId,
      formData,
      onCreated,
      recruitFormData,
      selectedWorkers,
      setIsLoading,
      setShowGigFormModal,
      t,
    });
  };

  const handleChangeTab = (type: CreateGigType) => {
    setFormData({ ...initialFormData, type });
    setRecruitFormData(initialRecruitFormData);
    if (type !== GigType.SOLO) {
      setSelectedWorkers([]);
    }

    setTab(type === GigType.SOLO ? "Schedule" : "Recruit");
    setShowChangeTabAlert(false);
  };

  const requestTabChange = (to: Tab) => {
    if (isLoading) return;
    if (to === "Recruit" && !canRecruit) return;

    if (to === "Schedule") {
      if (tab === "Recruit" && isRecruitDirty) {
        setChangeTo(GigType.SOLO);
        setShowChangeTabAlert(true);
      } else {
        setFormData((curr) => ({ ...curr, type: GigType.SOLO }));
        setTab("Schedule");
      }
      return;
    }

    if (tab === "Schedule" && isScheduleDirty) {
      setChangeTo(RECRUIT_TARGET_TYPE);
      setShowChangeTabAlert(true);
    } else {
      setFormData((curr) => ({ ...curr, type: RECRUIT_TARGET_TYPE }));
      setSelectedWorkers([]);
      setTab("Recruit");
    }
  };

  const handleRequestClose = useCallback(
    (_e?: unknown, reason?: "backdropClick" | "escapeKeyDown") => {
      if (reason === "backdropClick") {
        if (isScheduleDirty || isRecruitDirty) {
          setShowAlert(true);
        } else {
          closeModal();
        }
        return;
      }

      if (reason === "escapeKeyDown") {
        if (isScheduleDirty || isRecruitDirty) setShowAlert(true);
        else closeModal();
        return;
      }

      if (isScheduleDirty || isRecruitDirty) {
        setShowAlert(true);
      } else {
        closeModal();
      }
    },
    [isScheduleDirty, isRecruitDirty, closeModal],
  );

  const handleSubmitGigForm = (): void => {
    if (isLoading || !isFormValid || submitInFlightRef.current) {
      return;
    }
    submitInFlightRef.current = true;

    const submitPromise =
      tab === "Schedule" && selectedWorkers.length >= 2
        ? handleCreateParentGig()
        : handleCreateGig();
    void submitPromise.finally(() => {
      submitInFlightRef.current = false;
    });
  };

  return {
    canRecruit,
    changeTo,
    closeModal,
    formData,
    handleChangeTab,
    handleRequestClose,
    handleSubmitGigForm,
    havePublicModule,
    haveTeamModule,
    isLoading,
    overlapping,
    isAddingMoreShifts,
    totalHours,
    totalMinutes,
    hasInvalidShiftTimes,
    isPublicRecruit,
    visibleForValue,
    recruitFormData,
    requestTabChange,
    selectedWorkers,
    setFormData,
    setRecruitFormData,
    setSelectedWorkers,
    setShiftListOpen,
    setShowAlert,
    setShowChangeTabAlert,
    setIsAddingMoreShifts,
    shiftListOpen,
    showAlert,
    showChangeTabAlert,
    showOverlappingError,
    tab,
    isFormValid,
    createDisabledReasonKeys,
  };
};
